import React, { useEffect } from 'react';
import { RootStateOrAny, useSelector } from 'react-redux';
import {
  createStyles,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Select,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AntSwitch from '../EditEquipmentModal/AntSwitch';
import { useProductionSheetsStrings } from './constants/ProductionSheetsStrings';
import CommodityCategory from './CommodityCategory';
import { TCommodityCategory } from '../../types/commodity';
import { TCategoryProductSheet } from '../../types/product';
import { BRANDS } from '../../types/brands';
import { useTranslation } from 'react-i18next';
import { textToSnakeCase } from '../../utils/utils';

import { useFlags } from 'launchdarkly-react-client-sdk';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    switchWrapper: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  }),
);

interface ProductionSheetsProps {
  region?: string;
  productSheet: TCategoryProductSheet;
  testMarketFlag?: boolean;
  isEdit?: boolean;
  notForTestMarket?: boolean;
  setProductSheet: (productSheet: TCategoryProductSheet) => void;
  setIsTestMarketFlag: (isTestMarket: boolean) => void;
}

const ProductionSheets = ({
  region,
  productSheet,
  testMarketFlag,
  notForTestMarket,
  isEdit,
  setProductSheet,
  setIsTestMarketFlag,
}: ProductionSheetsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const ProductionSheetsStrings = useProductionSheetsStrings();
  const { isTestMarketOn } = useFlags();
  const selectedBrand = useSelector((state: RootStateOrAny) =>
    state.UIReducer.selectedBrand.toUpperCase(),
  );
  const unitsOfMeasures = useSelector((state: RootStateOrAny) =>
    state.UnitsOfMeasuresReducer.entities?.map(entity => {
      if (entity?.name) {
        entity.name = t(
          `app.common.shelfUnits.${textToSnakeCase(entity.name)}`,
          entity.name,
        );
      }
      return entity;
    }),
  );
  const shelfUnits = useSelector((state: RootStateOrAny) =>
    state.ShelfUnitsReducer.entities?.map(entity => {
      if (entity?.name) {
        entity.name = t(
          `app.common.shelfUnits.${textToSnakeCase(entity.name)}`,
          entity.name,
        );
      }
      return entity;
    }),
  );

  const { commodity } = useSelector(
    (state: RootStateOrAny) => state.CommodityGroupsReducer,
  );

  useEffect(() => {
    if (selectedBrand !== BRANDS.TH || productSheet.shelfLifeUnit) return;
    setProductSheet({
      ...productSheet,
      display: !!commodity?.psGroupData?.friendlyNameGroupIdk,
    });
  }, [commodity]);

  useEffect(() => {
    if (selectedBrand !== BRANDS.TH || productSheet.shelfLifeUnit) return;
    setProductSheet({ ...productSheet, shelfLifeUnit: shelfUnits[0]?.id });
  }, [productSheet, shelfUnits]);

  const handleSwitch = e => {
    switch (e.target.name) {
      case 'onProductionSheet':
        setProductSheet({
          ...productSheet,
          display: !productSheet.display,
          fresh: !!productSheet.fresh,
        });
        break;
      case 'freshCheck':
        setProductSheet({ ...productSheet, fresh: !productSheet.fresh });
        break;
      case 'testMarket':
        setIsTestMarketFlag(!testMarketFlag);
        break;
      default:
        break;
    }
  };

  const handleChangeSelect = e => {
    switch (e.target.name) {
      case 'shelfLife':
        setProductSheet({
          ...productSheet,
          shelfLife: e.target.value < 0 ? 0 : e.target.value,
        });
        break;
      case 'shelfLifeUnit':
        setProductSheet({ ...productSheet, shelfLifeUnit: e.target.value });
        break;
      case 'unitsOfMeasureDefault':
        setProductSheet({
          ...productSheet,
          unitsOfMeasure: {
            ...productSheet.unitsOfMeasure,
            default: +e.target.value,
          },
        });
        break;
      default:
        break;
    }
  };

  const handleChangeCategory = (category: TCommodityCategory) => {
    setProductSheet({
      ...productSheet,
      category,
    });
  };

  const onChangeMultiple = (event, values) => {
    let defaultUnit = productSheet.unitsOfMeasure.default;
    if (!values.some(value => value.id === defaultUnit)) {
      defaultUnit = values?.[0]?.id || 0;
    }
    setProductSheet({
      ...productSheet,
      unitsOfMeasure: {
        units: values,
        default: defaultUnit,
      },
    });
  };
  return selectedBrand === BRANDS.TH ? (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={4} className={classes.switchWrapper}>
            <Typography>
              {ProductionSheetsStrings.on_production_sheets}
            </Typography>
            <AntSwitch
              checked={!!productSheet.display}
              name="onProductionSheet"
              onChange={handleSwitch}
            />
          </Grid>
          {productSheet.display && (
            <Grid item xs={4} className={classes.switchWrapper}>
              <Typography>{ProductionSheetsStrings.fresh_check}</Typography>
              <AntSwitch
                checked={productSheet.fresh}
                name="freshCheck"
                onChange={handleSwitch}
              />
            </Grid>
          )}
          {region == 'NA' && isTestMarketOn && (
            <Grid item xs={4} className={classes.switchWrapper}>
              <Typography>{ProductionSheetsStrings.is_test_market}</Typography>
              <AntSwitch
                disabled={isEdit || notForTestMarket}
                checked={testMarketFlag}
                name="testMarket"
                onChange={handleSwitch}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      {productSheet.display && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <TextField
                variant="outlined"
                size="small"
                type="number"
                label={ProductionSheetsStrings.shelf_life}
                name="shelfLife"
                value={productSheet.shelfLife}
                InputProps={{ inputProps: { min: 1 } }}
                onChange={handleChangeSelect}
              />
            </Grid>
            <Grid item xs={3}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel htmlFor="age-native-simple" shrink>
                  {ProductionSheetsStrings.shelf_life_units}
                </InputLabel>
                <Select
                  native
                  value={productSheet.shelfLifeUnit}
                  onChange={handleChangeSelect}
                  inputProps={{
                    id: 'age-native-simple',
                    name: 'shelfLifeUnit',
                  }}
                >
                  {shelfUnits.map(unit => (
                    <option
                      aria-label={unit.name}
                      value={unit.id}
                      key={unit.id}
                    >
                      {unit.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                multiple
                id="tags-outlined"
                options={unitsOfMeasures}
                noOptionsText={t`app.common.noOptions`}
                value={(productSheet.unitsOfMeasure.units as any) || []}
                onChange={onChangeMultiple}
                getOptionLabel={(u: any) => u.name}
                filterSelectedOptions
                size="small"
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={ProductionSheetsStrings.units_of_measure}
                    placeholder={ProductionSheetsStrings.units_of_measure}
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel htmlFor="age-native-simple" shrink>
                  {ProductionSheetsStrings.units_of_measure_default}
                </InputLabel>
                <Select
                  native
                  value={productSheet.unitsOfMeasure.default}
                  onChange={handleChangeSelect}
                  inputProps={{
                    id: 'age-native-simple',
                    name: 'unitsOfMeasureDefault',
                  }}
                >
                  {productSheet.unitsOfMeasure.units.map(unit => (
                    <option
                      aria-label={unit.name}
                      value={unit.id}
                      key={unit.id}
                    >
                      {unit.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      )}
      {productSheet.display && productSheet.category && (
        <CommodityCategory
          region={region}
          category={productSheet.category}
          onChange={handleChangeCategory}
        />
      )}
    </Grid>
  ) : null;
};

export default ProductionSheets;
